import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, MessageSquare, ShieldCheck, Clock } from 'lucide-react';

import { BACKEND_URL } from '../config/api';

/**
 * Three contact modes a tradesperson can choose from. Labels are written to
 * match what the server actually enforces — no implied individual-worker
 * approval unless the mode genuinely requires it.
 */
const MODES = [
  {
    id: 'messages_only',
    label: 'Messages Only',
    icon: MessageSquare,
    desc: 'Employers can only message you in-app. Your phone number is never shared.',
    testId: 'contact-mode-messages-only',
  },
  {
    id: 'request_first',
    label: 'Request First (you approve each employer)',
    icon: Clock,
    desc: 'Approved employers must send you a request. You review each one and choose to approve or decline. You can revoke access later.',
    testId: 'contact-mode-request-first',
  },
  {
    id: 'approved_employers_can_contact',
    label: 'Any Approved Employer',
    icon: Phone,
    desc: "Any employer OnSiteHand has approved on the marketplace can see your phone (not individually approved by you). Rate-limited and audit-logged.",
    testId: 'contact-mode-approved-contact',
  },
];

export default function ContactModeCard() {
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/tradespeople/me/contact-mode`, {
          withCredentials: true,
        });
        setMode(r.data?.mode || 'messages_only');
      } catch (e) {
        setMode('messages_only');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = async (next) => {
    if (next === mode) return;
    const prev = mode;
    setMode(next);
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/tradespeople/me/contact-mode`,
        { mode: next },
        { withCredentials: true }
      );
      toast.success('Contact preference updated');
    } catch (e) {
      setMode(prev);
      toast.error('Could not update contact preference');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="card mb-6" data-testid="contact-mode-card">
      <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-orange-500" />
        Contact Preference
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Decide how employers can reach you. You can change this at any time —
        changing away from "Request First" revokes outstanding requests.
      </p>
      <div className="space-y-3">
        {MODES.map(({ id, label, icon: Icon, desc, testId }) => {
          const active = mode === id;
          return (
            <button
              key={id}
              type="button"
              disabled={saving}
              onClick={() => update(id)}
              className={`w-full text-left flex items-start gap-3 rounded-xl border p-4 transition-colors ${
                active
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
              data-testid={testId}
              aria-pressed={active}
            >
              <div
                className={`mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg ${
                  active ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-slate-500'
                }`}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900">{label}</p>
                <p className="text-sm text-slate-600 mt-0.5">{desc}</p>
              </div>
              <div
                className={`mt-1 h-4 w-4 flex-shrink-0 rounded-full border-2 ${
                  active ? 'border-orange-500 bg-orange-500' : 'border-slate-300'
                }`}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
